import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { uploadCVDraft, submitCandidatura } from "@/lib/api";
import { loadRecaptchaScript, getRecaptchaToken, getSiteKey } from "@/lib/recaptcha";
import { Upload, FileText, Check, Loader2, Plus, X, Link, FileUp, ChevronLeft, ChevronRight } from "lucide-react";

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const SENDING_MESSAGES = [
  "Se está procesando tu candidatura 🚀",
  "No tengas prisa, hablemos mientras...",
  "¿En qué año abandonó Apple Jony Ive?🍎",
  "Ya está casi...",
  "Qué prefieres ¿Cocacola o Pepsi ?🧐",
  "Nosotros Cocacola...🥤",
  "Se está procesando tu candidatura 🚀",
  "Pregunta rápida...",
  "¿Quién disparó primero en Star Wars? 👀",
  "Seguimos analizando tu CV...",
  "¿Sabías que el primer banner de internet tuvo un 44% de CTR? 📈",
  "Esto tarda menos que renderizar en After Effects...",
  "¿Mac o Windows? 💻",
  "Procesando tu talento...",
  "¿Qué logo costó 35$? (pista: Nike) 👟",
  "Un momento más...",
  "¿Batman o Iron Man? 🦇",
  "Seguimos...",
  "¿Qué marca convirtió una manzana mordida en un icono? 🍏",
  "Esto tarda menos que exportar un vídeo en 4K...",
  "¿Marvel o DC? 🎬",
  "Estamos revisando tu candidatura...",
  "¿Sabías que Netflix empezó enviando DVDs por correo? 📀",
  "Un segundo...",
  "¿PlayStation o Xbox? 🎮",
  "Analizando habilidades...",
  "¿Qué red social empezó como proyecto universitario de Zuckerberg? 📘",
  "Esto tarda menos que una reunión de marketing...",
  "¿Star Wars o El Señor de los Anillos? 🧙",
  "Procesando creatividad...",
  "¿Qué marca inventó el eslogan 'Just Do It'? 💪",
  "Casi estamos...",
  "¿Instagram o TikTok? 📱",
  "Escaneando tu CV...",
  "¿Qué serie tiene más dragones que presupuesto? 🐉",
  "Un momento más...",
  "¿Agencia o freelance? 😏",
  "Esto tarda menos que optimizar campañas de Google Ads...",
  "¿Qué empresa convirtió un garaje en un imperio tecnológico? 🚗",
  "Casi listo...",
  "¿Café o Monster para trabajar? ☕",
  "Seguimos...",
  "¿Cuál fue el primer vídeo de YouTube? 🎥",
  "Procesando experiencia...",
  "¿Qué empresa tiene una flecha escondida en su logo? 📦",
  "Últimos cálculos...",
  "¿Branding o performance? 🎯",
  "Estamos terminando...",
  "¿Cuál fue el primer iPhone? 📱",
  "Último paso...",
  "¿Ideas locas o estrategia perfecta? 🧠",
  "Candidatura casi lista...",
  "Preparando respuesta final...",
  "Gracias por tu paciencia 🙌",
];

function checkFileSize(file, isCarta = false) {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const label = isCarta ? "La carta" : "El currículum";
    return `${label} no puede superar ${MAX_FILE_SIZE_MB} MB (${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
  }
  return null;
}

export default function HomePage() {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState("");
  const [file, setFile] = useState(null);
  const [cartaFile, setCartaFile] = useState(null);
  const [portfolios, setPortfolios] = useState([""]);
  const [proyectoDestacado, setProyectoDestacado] = useState("");
  const [situacionActual, setSituacionActual] = useState("ninguno");
  const [comentario, setComentario] = useState("");
  const [expectativaMinK, setExpectativaMinK] = useState("");
  const [expectativaMaxK, setExpectativaMaxK] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [rotatingMessageIndex, setRotatingMessageIndex] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [dragActiveCarta, setDragActiveCarta] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [step, setStep] = useState(1);
  const [uploadId, setUploadId] = useState(null);
  const [uploadingDraft, setUploadingDraft] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const mounted = useRef(true);
  const STEPS = 4;

  useEffect(() => {
    if (!getSiteKey()) {
      setRecaptchaReady(true);
    } else {
      loadRecaptchaScript().then(() => {
        if (mounted.current) setRecaptchaReady(true);
      });
    }
    return () => { mounted.current = false; };
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type !== "application/pdf") {
        toast.error("Solo se permiten archivos PDF");
        return;
      }
      const err = checkFileSize(droppedFile, false);
      if (err) { toast.error(err); return; }
      setFile(droppedFile);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== "application/pdf") {
        toast.error("Solo se permiten archivos PDF");
        return;
      }
      const err = checkFileSize(selectedFile, false);
      if (err) { toast.error(err); return; }
      setFile(selectedFile);
    }
  };

  // Handlers for carta de presentación
  const handleDragCarta = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveCarta(true);
    } else if (e.type === "dragleave") {
      setDragActiveCarta(false);
    }
  };

  const handleDropCarta = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveCarta(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type !== "application/pdf") {
        toast.error("Solo se permiten archivos PDF");
        return;
      }
      const err = checkFileSize(droppedFile, true);
      if (err) { toast.error(err); return; }
      setCartaFile(droppedFile);
    }
  };

  const handleCartaFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== "application/pdf") {
        toast.error("Solo se permiten archivos PDF");
        return;
      }
      const err = checkFileSize(selectedFile, true);
      if (err) { toast.error(err); return; }
      setCartaFile(selectedFile);
    }
  };

  // Subir CV (y carta) al servidor en cuanto se seleccionan; no ejecuta IA (requiere reCAPTCHA)
  useEffect(() => {
    if (!file || !recaptchaReady) {
      if (!file) setUploadId(null);
      return;
    }
    let cancelled = false;
    setUploadingDraft(true);
    const doUpload = async () => {
      let token = null;
      if (getSiteKey()) {
        try {
          token = await getRecaptchaToken("upload_cv");
        } catch (err) {
          if (!cancelled) {
            toast.error("Error al verificar el captcha. Recarga la página e inténtalo de nuevo.");
            setUploadingDraft(false);
          }
          return;
        }
      }
      if (cancelled) return;
      uploadCVDraft(file, cartaFile || null, token)
        .then((data) => {
          if (!cancelled) setUploadId(data.upload_id);
        })
        .catch((err) => {
          if (!cancelled) {
            toast.error(err.response?.data?.detail || "Error al subir el currículum");
            setUploadId(null);
          }
        })
        .finally(() => {
          if (!cancelled) setUploadingDraft(false);
        });
    };
    doUpload();
    return () => { cancelled = true; };
  }, [file, cartaFile, recaptchaReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nombre.trim()) {
      toast.error("Por favor, introduce tu nombre");
      return;
    }
    
    if (!file) {
      toast.error("Por favor, sube tu CV en formato PDF");
      return;
    }
    if (!uploadId) {
      toast.error("Espera a que el currículum termine de subir o vuelve a seleccionarlo");
      return;
    }
    const cvSizeErr = checkFileSize(file, false);
    if (cvSizeErr) { toast.error(cvSizeErr); return; }
    if (cartaFile) {
      const cartaSizeErr = checkFileSize(cartaFile, true);
      if (cartaSizeErr) { toast.error(cartaSizeErr); return; }
    }

    if (!aiConsent) {
      toast.error("Necesitamos tu consentimiento para procesar la candidatura");
      return;
    }

    // Validate at least one portfolio
    const validPortfolios = portfolios.filter(p => p.trim());
    if (validPortfolios.length === 0) {
      toast.error("Por favor, introduce al menos un enlace a tu portfolio");
      return;
    }
    
    let recaptchaToken = null;
    if (getSiteKey()) {
      try {
        recaptchaToken = await getRecaptchaToken("submit_candidatura");
      } catch (err) {
        toast.error("Error al verificar el captcha. Recarga la página e inténtalo de nuevo.");
        return;
      }
    }
    setIsLoading(true);
    setRotatingMessageIndex(0);
    try {
      const minVal = expectativaMinK.trim().replace(/\D/g, "");
      const maxVal = expectativaMaxK.trim().replace(/\D/g, "");
      const rangoSalarial = minVal && maxVal ? `${minVal}-${maxVal}` : minVal || maxVal || null;
      await submitCandidatura(uploadId, nombre.trim(), validPortfolios, proyectoDestacado.trim() || null, situacionActual === "ninguno" ? null : situacionActual, comentario.trim() || null, recaptchaToken, rangoSalarial);
      setIsSuccess(true);
      toast.success("Candidatura enviada correctamente");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Error al enviar la candidatura");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setNombre("");
    setFile(null);
    setCartaFile(null);
    setPortfolios([""]);
    setProyectoDestacado("");
    setSituacionActual("ninguno");
    setComentario("");
    setUploadId(null);
    setStep(1);
    setIsSuccess(false);
  };

  const addPortfolio = () => {
    setPortfolios([...portfolios, ""]);
  };

  const removePortfolio = (index) => {
    if (portfolios.length > 1) {
      setPortfolios(portfolios.filter((_, i) => i !== index));
    } else {
      setPortfolios([""]);
    }
  };

  const updatePortfolio = (index, value) => {
    const updated = [...portfolios];
    updated[index] = value;
    setPortfolios(updated);
  };

  useEffect(() => {
    if (!isLoading) return;
    const id = setInterval(
      () => setRotatingMessageIndex((i) => (i + 1) % SENDING_MESSAGES.length),
      2800
    );
    return () => clearInterval(id);
  }, [isLoading]);

  if (isSuccess) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6 bg-fondo-candidatos">
        <div className="w-full max-w-md text-center animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-foreground flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-background" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2" data-testid="success-title">
            Candidatura enviada
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Hemos recibido tu CV correctamente. Nos pondremos en contacto contigo.
          </p>
          <Button
            variant="outline"
            onClick={handleReset}
            className="text-sm"
            data-testid="send-another-btn"
          >
            Enviar otra candidatura
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center p-6 bg-fondo-candidatos">
      {/* Mobile: formulario único con scroll */}
      <div className="w-full max-w-md animate-fade-in md:hidden relative min-h-[420px]">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-5">
            <img src="/logo.png" alt="Talento Brutal" className="h-12 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2" data-testid="home-title">Talento Brutal</h1>
          <p className="text-sm text-muted-foreground">
            Gracias por querer trabajar con nosotros. Envíanos tu candidatura y nos pondremos en contacto contigo en cuanto revisemos la solicitud.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="nombre" className="text-sm font-medium">Nombre completo</Label>
            <Input id="nombre" type="text" placeholder="Tu nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} disabled={isLoading} className="h-11" data-testid="nombre-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Situación actual</Label>
            <Select value={situacionActual} onValueChange={setSituacionActual} disabled={isLoading}>
              <SelectTrigger className="h-11 w-full" data-testid="situacion-select-mobile">
                <SelectValue placeholder="Elige una opción" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="freelance">Freelance</SelectItem>
                <SelectItem value="asalariado">Asalariado/a</SelectItem>
                <SelectItem value="estudiante">Estudiante</SelectItem>
                <SelectItem value="ninguno">Ninguno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Fila 1: Currículum y Carta */}
          <div className="grid grid-cols-2 gap-3">
            {/* Currículum */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Currículum <span className="text-muted-foreground font-normal">(PDF, máx. 5 MB)</span>
              </Label>
              <div
                className={`
                  relative border border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer h-[110px] flex items-center justify-center
                  ${dragActive ? "border-foreground bg-muted/50" : "border-input hover:border-muted-foreground/50"}
                  ${file ? "border-foreground bg-muted/30" : ""}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById("cv-file").click()}
                data-testid="cv-dropzone"
              >
                <input
                  id="cv-file"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isLoading}
                  data-testid="cv-file-input"
                />
                {file ? (
                  <div className="flex flex-col items-center gap-1 w-full">
                    <FileText className="w-5 h-5 text-foreground shrink-0" strokeWidth={1.5} />
                    <span className="text-xs font-medium truncate w-full px-1 text-center">
                      {file.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Cambiar
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Upload className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground text-center">
                      Arrastra tu CV<br />o haz clic
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Carta de presentación */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Carta <span className="text-muted-foreground font-normal">(opcional, máx. 5 MB)</span>
              </Label>
              <div
                className={`
                  relative border border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer h-[110px] flex items-center justify-center
                  ${dragActiveCarta ? "border-foreground bg-muted/50" : "border-input hover:border-muted-foreground/50"}
                  ${cartaFile ? "border-foreground bg-muted/30" : ""}
                `}
                onDragEnter={handleDragCarta}
                onDragLeave={handleDragCarta}
                onDragOver={handleDragCarta}
                onDrop={handleDropCarta}
                onClick={() => document.getElementById("carta-file").click()}
                data-testid="carta-dropzone"
              >
                <input
                  id="carta-file"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleCartaFileChange}
                  className="hidden"
                  disabled={isLoading}
                  data-testid="carta-file-input"
                />
                {cartaFile ? (
                  <div className="flex flex-col items-center gap-1 w-full">
                    <FileUp className="w-5 h-5 text-foreground shrink-0" strokeWidth={1.5} />
                    <span className="text-xs font-medium truncate w-full px-1 text-center">
                      {cartaFile.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Cambiar
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <FileUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground text-center">
                      Arrastra tu carta<br />o haz clic
                    </span>
                    <span className="text-xs text-muted-foreground/70">PDF, máx. 5 MB</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Fila 2: Portfolio y resto */}
          {/* Portfolio links */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Enlace a portfolio
            </Label>
            <div className="space-y-2">
              {portfolios.map((portfolio, index) => (
                <div key={index} className="flex gap-2">
                  <div className="relative flex-1">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      type="url"
                      placeholder="https://tu-portfolio.com"
                      value={portfolio}
                      onChange={(e) => updatePortfolio(index, e.target.value)}
                      disabled={isLoading}
                      className="h-11 pl-10"
                      data-testid={`portfolio-input-${index}`}
                    />
                  </div>
                  {portfolios.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePortfolio(index)}
                      disabled={isLoading}
                      className="h-11 w-11 shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                  {index === portfolios.length - 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={addPortfolio}
                      disabled={isLoading}
                      className="h-11 w-11 shrink-0"
                      data-testid="add-portfolio-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Proyecto destacado */}
          <div className="space-y-2">
            <Label className="text-sm font-medium" htmlFor="proyecto-destacado">
              Tu proyecto más destacado
            </Label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                id="proyecto-destacado"
                type="url"
                placeholder="https://..."
                value={proyectoDestacado}
                onChange={(e) => setProyectoDestacado(e.target.value)}
                disabled={isLoading}
                className="h-11 pl-10"
              />
            </div>
          </div>

          {/* Expectativa salarial */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Expectativa salarial</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="expectativa-min-mobile" className="text-xs text-muted-foreground">Mínimo</Label>
                <div className="flex items-center gap-1">
                  <Input
                    id="expectativa-min-mobile"
                    type="text"
                    inputMode="numeric"
                    value={expectativaMinK}
                    onChange={(e) => setExpectativaMinK(e.target.value.replace(/[^\d]/g, ""))}
                    disabled={isLoading}
                    className="h-11"
                  />
                  <span className="text-sm text-muted-foreground shrink-0">€</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="expectativa-max-mobile" className="text-xs text-muted-foreground">Máximo</Label>
                <div className="flex items-center gap-1">
                  <Input
                    id="expectativa-max-mobile"
                    type="text"
                    inputMode="numeric"
                    value={expectativaMaxK}
                    onChange={(e) => setExpectativaMaxK(e.target.value.replace(/[^\d]/g, ""))}
                    disabled={isLoading}
                    className="h-11"
                  />
                  <span className="text-sm text-muted-foreground shrink-0">€</span>
                </div>
              </div>
            </div>
          </div>

          {/* ¿Algo que quieras comentarnos? */}
          <div className="space-y-2">
            <Label className="text-sm font-medium" htmlFor="comentario-mobile">
              ¿Algo que quieras comentarnos sobre ti?
            </Label>
            <Textarea
              id="comentario-mobile"
              placeholder="Cuéntanos lo que consideres relevante: motivación, disponibilidad, algo que te defina..."
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              disabled={isLoading}
              className="min-h-[100px] resize-y"
              data-testid="comentario-textarea"
            />
          </div>

          {/* Consentimiento IA */}
          <div className="flex items-start gap-3 rounded-lg border border-input p-4 bg-muted/30">
            <Checkbox
              id="ai-consent"
              checked={aiConsent}
              onCheckedChange={setAiConsent}
              disabled={isLoading}
              className="mt-0.5 shrink-0"
              data-testid="ai-consent-checkbox"
            />
            <label
              htmlFor="ai-consent"
              className="text-xs text-muted-foreground leading-relaxed cursor-pointer select-none"
            >
              He leído y acepto la{" "}
              <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline" onClick={(e) => e.stopPropagation()}>
                política de privacidad de Brutal SC
              </a>
              {" "}para el envío de la candidatura.
            </label>
          </div>

          <Button
            type="submit"
            className="w-full h-11 text-sm font-medium"
            disabled={isLoading || !file || !uploadId || uploadingDraft}
            data-testid="submit-btn"
          >
            {uploadingDraft ? (
              "Subiendo currículum..."
            ) : isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando CV...
              </>
            ) : (
              "Enviar candidatura"
            )}
          </Button>
        </form>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-lg backdrop-blur-md bg-background/95 p-6">
            <h2 className="text-xl font-semibold">Enviando</h2>
            <p key={rotatingMessageIndex} className="text-sm text-muted-foreground text-center max-w-[280px] animate-fade-in">
              {SENDING_MESSAGES[rotatingMessageIndex]}
            </p>
            <p className="text-xs text-muted-foreground/80 text-center">
              Las candidaturas tardan 2-3 minutos en enviarse
            </p>
          </div>
        )}
      </div>

      {/* Desktop: formulario por pasos dentro de una caja */}
      <div className="hidden md:flex w-full max-w-lg animate-fade-in overflow-visible">
        <div className={`w-full rounded-xl border bg-card shadow-sm p-8 relative ${isLoading ? "form-card-loading" : "border-[1px]"}`}>
          {/* Logo + título + intro (siempre visibles) */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <img src="/logo.png" alt="Talento Brutal" className="h-12 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight mb-2">Talento Brutal</h1>
            <p className="text-sm text-muted-foreground">
              Gracias por querer trabajar con nosotros. Envíanos tu candidatura y nos pondremos en contacto contigo.
            </p>
          </div>

          {/* Indicador de pasos */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                    step === s ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {s}
                </span>
                {s < STEPS && <span className="h-px w-4 bg-muted-foreground/30" />}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Área de contenido: altura natural (sin recorte) + fade-in al cambiar de paso */}
            <div key={step} className="animate-fade-in">
                {/* Step 1: nombre + situación */}
                {step === 1 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="nombre-desktop" className="text-sm font-medium">Nombre completo</Label>
                    <Input id="nombre-desktop" type="text" placeholder="Tu nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} disabled={isLoading} className="h-11" data-testid="nombre-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Situación actual</Label>
                    <Tabs value={situacionActual} onValueChange={setSituacionActual} className="w-full">
<TabsList className="w-full grid grid-cols-4 h-11">
                        <TabsTrigger value="freelance" disabled={isLoading} className="text-sm">Freelance</TabsTrigger>
                        <TabsTrigger value="asalariado" disabled={isLoading} className="text-sm">Asalariado/a</TabsTrigger>
                        <TabsTrigger value="estudiante" disabled={isLoading} className="text-sm">Estudiante</TabsTrigger>
                        <TabsTrigger value="ninguno" disabled={isLoading} className="text-sm">Ninguno</TabsTrigger>
                    </TabsList>
                    </Tabs>
                  </div>
                </div>
              )}

              {/* Step 2: Añade tus archivos */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-center mt-4">Añade tus archivos</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Currículum <span className="text-muted-foreground font-normal">(PDF, máx. 5 MB)</span></Label>
                    <div
                      className={`relative border border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer h-[110px] flex items-center justify-center ${dragActive ? "border-foreground bg-muted/50" : "border-input hover:border-muted-foreground/50"} ${file ? "border-foreground bg-muted/30" : ""}`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById("cv-file-desktop")?.click()}
                      data-testid="cv-dropzone"
                    >
                      <input id="cv-file-desktop" type="file" accept=".pdf,application/pdf" onChange={handleFileChange} className="hidden" disabled={isLoading} data-testid="cv-file-input" />
                      {file ? (
                        <div className="flex flex-col items-center gap-1 w-full">
                          <FileText className="w-5 h-5 text-foreground shrink-0" strokeWidth={1.5} />
                          <span className="text-xs font-medium truncate w-full px-1 text-center">{file.name}</span>
                          <span className="text-xs text-muted-foreground">Cambiar</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                          <span className="text-xs text-muted-foreground text-center">Arrastra tu CV<br />o haz clic</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Carta <span className="text-muted-foreground font-normal">(opcional, máx. 5 MB)</span></Label>
                    <div
                      className={`relative border border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer h-[110px] flex items-center justify-center ${dragActiveCarta ? "border-foreground bg-muted/50" : "border-input hover:border-muted-foreground/50"} ${cartaFile ? "border-foreground bg-muted/30" : ""}`}
                      onDragEnter={handleDragCarta}
                      onDragLeave={handleDragCarta}
                      onDragOver={handleDragCarta}
                      onDrop={handleDropCarta}
                      onClick={() => document.getElementById("carta-file-desktop")?.click()}
                      data-testid="carta-dropzone"
                    >
                      <input id="carta-file-desktop" type="file" accept=".pdf,application/pdf" onChange={handleCartaFileChange} className="hidden" disabled={isLoading} data-testid="carta-file-input" />
                      {cartaFile ? (
                        <div className="flex flex-col items-center gap-1 w-full">
                          <FileUp className="w-5 h-5 text-foreground shrink-0" strokeWidth={1.5} />
                          <span className="text-xs font-medium truncate w-full px-1 text-center">{cartaFile.name}</span>
                          <span className="text-xs text-muted-foreground">Cambiar</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <FileUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                          <span className="text-xs text-muted-foreground text-center">Arrastra tu carta<br />o haz clic</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

              {/* Step 3: Portfolio y proyecto destacado */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-center mt-4">Enlaces</h2>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Enlace a portfolio</Label>
                  <div className="space-y-2">
                    {portfolios.map((portfolio, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="relative flex-1">
                          <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                          <Input type="url" placeholder="https://tu-portfolio.com" value={portfolio} onChange={(e) => updatePortfolio(index, e.target.value)} disabled={isLoading} className="h-11 pl-10" data-testid={`portfolio-input-${index}`} />
                        </div>
                        {portfolios.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removePortfolio(index)} disabled={isLoading} className="h-11 w-11 shrink-0"><X className="w-4 h-4" /></Button>
                        )}
                        {index === portfolios.length - 1 && (
                          <Button type="button" variant="outline" size="icon" onClick={addPortfolio} disabled={isLoading} className="h-11 w-11 shrink-0" data-testid="add-portfolio-btn"><Plus className="w-4 h-4" /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium" htmlFor="proyecto-destacado-desktop">Tu proyecto más destacado</Label>
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <Input id="proyecto-destacado-desktop" type="url" placeholder="https://..." value={proyectoDestacado} onChange={(e) => setProyectoDestacado(e.target.value)} disabled={isLoading} className="h-11 pl-10" />
                  </div>
                </div>
              </div>
            )}

              {/* Step 4: Expectativa salarial + Comentario + consentimiento y enviar */}
              {step === 4 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-center mt-4">Último paso</h2>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Expectativa salarial</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expectativa-min-desktop" className="text-xs text-muted-foreground">Mínimo</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          id="expectativa-min-desktop"
                          type="text"
                          inputMode="numeric"
                          value={expectativaMinK}
                          onChange={(e) => setExpectativaMinK(e.target.value.replace(/[^\d]/g, ""))}
                          disabled={isLoading}
                          className="h-11"
                        />
                        <span className="text-sm text-muted-foreground shrink-0">€</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expectativa-max-desktop" className="text-xs text-muted-foreground">Máximo</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          id="expectativa-max-desktop"
                          type="text"
                          inputMode="numeric"
                          value={expectativaMaxK}
                          onChange={(e) => setExpectativaMaxK(e.target.value.replace(/[^\d]/g, ""))}
                          disabled={isLoading}
                          className="h-11"
                        />
                        <span className="text-sm text-muted-foreground shrink-0">€</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="comentario-desktop" className="text-sm font-medium">¿Algo que quieras comentarnos sobre ti?</Label>
                  <Textarea
                    id="comentario-desktop"
                    placeholder="Cuéntanos lo que consideres relevante: motivación, disponibilidad, algo que te defina..."
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    disabled={isLoading}
                    className="min-h-[100px] resize-y"
                    data-testid="comentario-textarea"
                  />
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-input p-4 bg-muted/30">
                  <Checkbox id="ai-consent-desktop" checked={aiConsent} onCheckedChange={setAiConsent} disabled={isLoading} className="mt-0.5 shrink-0" data-testid="ai-consent-checkbox" />
                  <label htmlFor="ai-consent-desktop" className="text-xs text-muted-foreground leading-relaxed cursor-pointer select-none">
                    He leído y acepto la{" "}
                    <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline" onClick={(e) => e.stopPropagation()}>
                      política de privacidad de Brutal SC
                    </a>
                    {" "}para el envío de la candidatura.
                  </label>
                </div>
              </div>
            )}
            </div>

            {/* Navegación: Anterior / Siguiente o Enviar */}
            <div className="flex items-center justify-between pt-4 border-t mt-8">
              <div>
                {step > 1 ? (
                  <Button type="button" variant="ghost" onClick={() => requestAnimationFrame(() => setStep((s) => s - 1))} className="gap-2">
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </Button>
                ) : (
                  <span />
                )}
              </div>
              <div>
                {step < STEPS ? (
                  <Button
                    type="button"
                    disabled={(step === 1 && !nombre.trim()) || (step === 2 && !file)}
                    onClick={() => requestAnimationFrame(() => setStep((s) => s + 1))}
                    className="gap-2"
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button type="submit" className="gap-2" disabled={isLoading || !file || !uploadId || uploadingDraft} data-testid="submit-btn">
                    {uploadingDraft ? <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo currículum...</> : isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : "Enviar candidatura"}
                  </Button>
                )}
              </div>
            </div>
          </form>
          {isLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-xl backdrop-blur-md bg-card/95 p-8">
              <h2 className="text-xl font-semibold">Enviando</h2>
              <p key={rotatingMessageIndex} className="text-sm text-muted-foreground text-center max-w-[320px] animate-fade-in">
                {SENDING_MESSAGES[rotatingMessageIndex]}
              </p>
              <p className="text-xs text-muted-foreground/80 text-center">
                Las candidaturas tardan 2-3 minutos en enviarse
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
